import type { LocaleMessages, User } from 'shared-types';

type UserPermissions = User['permissions'];
type LabelByPermission = Record<
  UserPermissions[number],
  { label: string; helpText?: string }
>;

interface Params {
  messages?: LocaleMessages;
}

// overloads
function getUserPermissions(
  params: Params & { messages: LocaleMessages }
): LabelByPermission;
function getUserPermissions(
  params: Params & { messages?: never }
): UserPermissions;

// implementation
function getUserPermissions({
  messages
}: Params): LabelByPermission | UserPermissions {
  if (messages) {
    return {
      'create-courses': {
        label: messages.usersPermissionCreateCourses
      },
      'create-groups': {
        label: messages.usersPermissionCreateGroups
      },
      'create-paths': {
        label: messages.usersPermissionCreatePaths
      },
      'create-achievements': {
        label: messages.usersPermissionCreateAchievements
      },
      'create-events': {
        label: messages.usersPermissionCreateEvents
      },
      'manage-users': {
        helpText: messages.usersPermissionManageUsersInfo,
        label: messages.usersPermissionManageUsers
      },
      'edit-user-custom-attributes': {
        label: messages.usersPermissionEditUserCustomAttributes
      },
      'view-all-users': {
        helpText: messages.usersPermissionViewAllUsersInfo,
        label: messages.usersPermissionViewAllUsers
      },
      'use-ai-tools': {
        label: messages.usersPermissionUseAITools,
        helpText: messages.usersPermissionUseAIToolsInfo
      },
      'manage-email-templates': {
        label: messages.usersPermissionSettingsManageEmailTemplates
      },
      'manage-competencies': {
        helpText: messages.usersPermissionManageCompetenciesInfo,
        label: messages.usersPermissionManageCompetencies
      },
      'manage-competency-profiles': {
        helpText: messages.usersPermissionManageCompetencyProfilesInfo,
        label: messages.usersPermissionManageCompetencyProfiles
      },
      'grant-competencies': {
        helpText: messages.usersPermissionGrantCompetenciesInfo,
        label: messages.usersPermissionGrantCompetencies
      }
    } satisfies LabelByPermission;
  }

  return [
    'create-courses',
    'create-groups',
    'create-paths',
    'create-achievements',
    'create-events',
    'manage-users',
    'edit-user-custom-attributes',
    'view-all-users',
    'use-ai-tools',
    'manage-email-templates',
    'manage-competencies',
    'manage-competency-profiles',
    'grant-competencies'
  ] as const satisfies UserPermissions;
}

export default getUserPermissions;
